import React, { useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

const DataFlowVisualization = () => {
  const canvasRef = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];
    let nodes = [];

    // Theme-aware colors
    const colors = {
      primary: '#FF3621',
      secondary: theme === 'dark' ? '#4ADE80' : '#16A34A',
      nodeGlow: theme === 'dark' ? 'rgba(255, 54, 33, 0.3)' : 'rgba(255, 54, 33, 0.2)',
      connectionStart: theme === 'dark' ? 'rgba(255, 54, 33, 0.1)' : 'rgba(255, 54, 33, 0.15)',
      connectionMid: theme === 'dark' ? 'rgba(255, 54, 33, 0.2)' : 'rgba(255, 54, 33, 0.25)',
      nodeHighlight: 'rgba(255, 255, 255, 0.3)'
    };

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    resize();
    window.addEventListener('resize', resize);

    // Create neural network nodes
    const createNodes = () => {
      nodes = [];
      const width = canvas.offsetWidth;
      const height = canvas.offsetHeight;
      const layers = [3, 5, 6, 5, 3]; // Neural network structure
      const layerSpacing = width / (layers.length + 1);

      layers.forEach((nodeCount, layerIndex) => {
        const x = layerSpacing * (layerIndex + 1);
        const nodeSpacing = height / (nodeCount + 1);

        for (let i = 0; i < nodeCount; i++) {
          nodes.push({
            x,
            y: nodeSpacing * (i + 1),
            radius: 6 + Math.random() * 4,
            layer: layerIndex,
            index: i,
            pulse: Math.random() * Math.PI * 2,
            connections: []
          });
        }
      });

      // Create connections between adjacent layers
      nodes.forEach(node => {
        const nextLayerNodes = nodes.filter(n => n.layer === node.layer + 1);
        nextLayerNodes.forEach(targetNode => {
          if (Math.random() > 0.3) { // 70% chance of connection
            node.connections.push(targetNode);
          }
        });
      });
    };

    // Create data particles
    const createParticle = () => {
      const startNode = nodes.find(n => n.layer === 0);
      if (!startNode) return null;

      const sourceNode = nodes.filter(n => n.layer === 0)[Math.floor(Math.random() * 3)];
      
      return {
        x: 0,
        y: sourceNode ? sourceNode.y : Math.random() * canvas.offsetHeight,
        targetNode: sourceNode,
        speed: 1 + Math.random() * 2,
        size: 2 + Math.random() * 2,
        alpha: 0.8,
        color: Math.random() > 0.5 ? '#FF3621' : '#4ADE80',
        trail: [],
        currentLayer: -1
      };
    };

    createNodes();

    // Initialize particles
    for (let i = 0; i < 15; i++) {
      const p = createParticle();
      if (p) particles.push(p);
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      // Draw connections with gradient
      nodes.forEach(node => {
        node.connections.forEach(targetNode => {
          const gradient = ctx.createLinearGradient(node.x, node.y, targetNode.x, targetNode.y);
          gradient.addColorStop(0, colors.connectionStart);
          gradient.addColorStop(0.5, colors.connectionMid);
          gradient.addColorStop(1, colors.connectionStart);

          ctx.beginPath();
          ctx.moveTo(node.x, node.y);
          ctx.lineTo(targetNode.x, targetNode.y);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 1;
          ctx.stroke();
        });
      });

      // Draw and animate nodes
      nodes.forEach(node => {
        node.pulse += 0.03;
        const pulseSize = Math.sin(node.pulse) * 2;

        // Outer glow
        const glowGradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, node.radius + pulseSize + 15
        );
        glowGradient.addColorStop(0, colors.nodeGlow);
        glowGradient.addColorStop(0.5, theme === 'dark' ? 'rgba(255, 54, 33, 0.1)' : 'rgba(255, 54, 33, 0.08)');
        glowGradient.addColorStop(1, 'rgba(255, 54, 33, 0)');

        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius + pulseSize + 15, 0, Math.PI * 2);
        ctx.fillStyle = glowGradient;
        ctx.fill();

        // Main node
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius + pulseSize, 0, Math.PI * 2);
        ctx.fillStyle = colors.primary;
        ctx.fill();

        // Inner highlight
        ctx.beginPath();
        ctx.arc(node.x - 2, node.y - 2, (node.radius + pulseSize) * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = colors.nodeHighlight;
        ctx.fill();
      });

      // Update and draw particles
      particles.forEach((particle, index) => {
        // Store trail position
        particle.trail.unshift({ x: particle.x, y: particle.y });
        if (particle.trail.length > 10) particle.trail.pop();

        // Draw trail
        particle.trail.forEach((pos, i) => {
          const trailAlpha = (1 - i / particle.trail.length) * particle.alpha * 0.5;
          const trailColor = particle.color === '#FF3621' 
            ? `rgba(255, 54, 33, ${trailAlpha})` 
            : theme === 'dark' 
              ? `rgba(74, 222, 128, ${trailAlpha})`
              : `rgba(22, 163, 74, ${trailAlpha})`;
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, particle.size * (1 - i / particle.trail.length), 0, Math.PI * 2);
          ctx.fillStyle = trailColor;
          ctx.fill();
        });

        // Draw particle with glow
        const particleGlowColor = particle.color === '#FF3621'
          ? 'rgba(255, 54, 33, 0.3)'
          : theme === 'dark' ? 'rgba(74, 222, 128, 0.3)' : 'rgba(22, 163, 74, 0.3)';
        
        const particleGlow = ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, particle.size * 3
        );
        particleGlow.addColorStop(0, particle.color === '#FF3621' ? colors.primary : colors.secondary);
        particleGlow.addColorStop(0.5, particleGlowColor);
        particleGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * 3, 0, Math.PI * 2);
        ctx.fillStyle = particleGlow;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = particle.color === '#FF3621' ? colors.primary : colors.secondary;
        ctx.fill();

        // Move particle towards target node
        if (particle.targetNode) {
          const dx = particle.targetNode.x - particle.x;
          const dy = particle.targetNode.y - particle.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 10) {
            // Reached node, find next target
            const nextConnections = particle.targetNode.connections;
            if (nextConnections.length > 0) {
              particle.targetNode = nextConnections[Math.floor(Math.random() * nextConnections.length)];
              particle.currentLayer++;
            } else {
              // Reached end, reset particle
              particles[index] = createParticle();
            }
          } else {
            particle.x += (dx / dist) * particle.speed;
            particle.y += (dy / dist) * particle.speed;
          }
        } else {
          particle.x += particle.speed;
          if (particle.x > canvas.offsetWidth) {
            particles[index] = createParticle();
          }
        }
      });

      // Add new particles occasionally
      if (Math.random() > 0.97 && particles.length < 25) {
        const p = createParticle();
        if (p) particles.push(p);
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full opacity-60"
      style={{ pointerEvents: 'none' }}
    />
  );
};

export default DataFlowVisualization;
